import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { defectsKeys, type DefectWithDetails } from "@/hooks/useDefectsQuery";

/** Response of the sap-integrations/push-mangel Edge Function (TES-528/1630). Local-only (not re-exported). */
interface SapPushMangelResult {
  queued: boolean;
  duplicate: boolean;
  outbox?: { id: string; status: string } | null;
}

type CachedLists = [readonly unknown[], DefectWithDetails[] | undefined][];

/**
 * TES-545 — trigger a SAP QM Q-Meldung push for a defect.
 *
 * Calls the sap-integrations/push-mangel Edge Function (which enqueues a sap_sync_outbox event;
 * RLS + role_permissions decide whether the caller may push). Optimistically flips the defect's
 * sap_sync_status to "pending" in every cached defect list, rolls back on error, and revalidates
 * on settle. The Retry path is the caller invoking mutate() again (the error toast offers it).
 */
export function useSapPushMangel(defectId: string) {
  const queryClient = useQueryClient();

  return useMutation<SapPushMangelResult, Error, void, { previous: CachedLists }>({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<SapPushMangelResult>(
        "sap-integrations/push-mangel",
        { body: { defect_id: defectId } },
      );
      if (error) throw error;
      if (!data) throw new Error("sap-push: empty response");
      return data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: defectsKeys.all });
      const previous = queryClient.getQueriesData<DefectWithDetails[]>({
        queryKey: defectsKeys.all,
      }) as CachedLists;
      queryClient.setQueriesData<DefectWithDetails[]>({ queryKey: defectsKeys.all }, (old) =>
        old?.map((d) => (d.id === defectId ? { ...d, sap_sync_status: "pending" } : d)),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      logger.error("SAP push-mangel failed:", error);
      context?.previous?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: defectsKeys.all });
    },
  });
}
